// Deterministic daily randomness for the Explore collections: a PRNG seeded by
// today's (local) date, so the order is stable if you revisit within the day
// but refreshes tomorrow. Hand-rolled — a seeded-RNG dependency can't earn its
// place for a mulberry32 one-liner.

// FNV-1a string hash → 32-bit seed.
const hashSeed = (s: string): number => {
	let h = 2166136261
	for (let i = 0; i < s.length; i++) {
		h ^= s.charCodeAt(i)
		h = Math.imul(h, 16777619)
	}
	return h >>> 0
}

// mulberry32 — tiny, fast, good-enough PRNG returning [0, 1).
const mulberry32 = (seed: number): (() => number) => {
	let a = seed
	return () => {
		a = (a + 0x6d2b79f5) | 0
		let t = Math.imul(a ^ (a >>> 15), 1 | a)
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296
	}
}

// A fresh RNG stream seeded by the local calendar day (YYYY-M-D).
export const dailyRng = (): (() => number) => {
	const d = new Date()
	return mulberry32(
		hashSeed(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`),
	)
}

// Light, order-preserving reorder: each item keeps its index plus a small
// seeded jitter, so the result stays close to the input order (the meaningful
// nearest-/newest-first sort survives) but varies a little day to day.
// `strength` is roughly how many slots an item may drift.
export const jitter = <T>(
	items: T[],
	rng: () => number,
	strength: number,
): T[] =>
	items
		.map((item, i) => ({ item, key: i + (rng() - 0.5) * 2 * strength }))
		.sort((a, b) => a.key - b.key)
		.map((x) => x.item)
